import { Spine } from "pixi-spine";
import { Spine as Spine4, Skin } from "@pixi-spine/runtime-4.1";
import { IServiceProvider } from "@westpierstudio/luma/services";
import { Container, DisplayObject, Graphics, RenderTexture, SCALE_MODES, Sprite, Text, Texture } from "pixi.js";
import { Tween } from "@westpierstudio/luma/createjs";
import { BonusSymbolID, CoreSymbolID, ReelSetSymbols, ReelSymbols, WildSymbolID, Win } from "servertypes/SharedTypes";
import { ReelSymbol } from "components/reels/ReelSymbol";
import { sample } from "lodash";
import { CrossbowBonus, CrossbowBonusSpec } from "components/CrossbowBonus";

//Handles negative values
export function mod(a: number, b: number): number {
    return ((a % b) + b) % b;
}

export function randomBool() {
    return Math.random() < 0.5;
}

export function range(min: number, max: number): number[] {
    const range: number[] = [];
    for (let i = min; i <= max; i++) {
        range.push(i);
    }
    return range;
}

export function devLog(msg: any): void {

    if (process.env.NODE_ENV !== `production`) {
        console.log(msg);
    }
}

export function devLogWarn(msg: any): void {

    if (process.env.NODE_ENV !== `production`) {
        console.warn(msg);
    }
}

export function toRad(angle: number): number {
    return angle * (Math.PI / 180);
}

export function toDeg(angle: number): number {
    return angle * (180 / Math.PI);
}

export function textCountUp(item: Text, startValue: number, endValue: number, time: number, fractionDigits = 0, onChange?: Function, onComplete?: Function): { value: number } {

    const countUpObject = { value: startValue };

    item.text = `${startValue}`;

    const change = () => {

        item.text = countUpObject.value.toFixed(fractionDigits);

        if (onChange) {
            onChange();
        }
    };

    const tween = Tween.get(countUpObject);
    tween.to({ value: endValue }, time);
    tween.addEventListener(`change`, () => change());

    if (onComplete) {
        tween.addEventListener(`complete`, () => onComplete());
    }

    return countUpObject;
}

export function copyToClipboard(str: string): void {

    const textArea = document.createElement(`textarea`);
    textArea.value = str;
    textArea.setAttribute(`readonly`, ``);
    textArea.style.position = `absolute`;
    textArea.style.left = `-9999px`;
    document.body.appendChild(textArea);

    const selection = (document as Document).getSelection() as Selection;

    const selected = selection.rangeCount > 0 ? selection.getRangeAt(0) : false;

    textArea.select();
    document.execCommand(`copy`);
    document.body.removeChild(textArea);

    if (selected) {

        selection.removeAllRanges();
        selection.addRange(selected);
    }
}

export function drawRect(width: number, height: number, colour: number, alpha = 1.0): Graphics {

    const graphic = new Graphics();

    graphic.beginFill(colour, alpha);
    graphic.drawRect(0, 0, width, height);
    graphic.endFill();

    return graphic;
}
export function drawRoundedRect(width: number, height: number, colour: number, radius: number, alpha = 1.0): Graphics {

    const graphic = new Graphics();

    graphic.beginFill(colour, alpha);
    graphic.drawRoundedRect(0, 0, width, height, radius);
    graphic.endFill();

    return graphic;
}

export function drawRectCentered(width: number, height: number, colour: number, alpha = 1.0): Graphics {

    const graphic = new Graphics();

    graphic.beginFill(colour, alpha);
    graphic.drawRect(-width / 2, -height / 2, width, height);
    graphic.endFill();

    return graphic;
}

export function drawBox(boxWidth: number, boxHeight: number, lineWidth: number, colour: number): Graphics {

    const graphic = new Graphics();

    graphic.lineStyle(lineWidth, colour, 1.0, 0);

    graphic.moveTo(0, 0);

    graphic.lineTo(boxWidth, 0);
    graphic.lineTo(boxWidth, boxHeight);
    graphic.lineTo(0, boxHeight);
    graphic.lineTo(0, 0);

    return graphic;
}

function drawCircle(radius: number, colour: number, alpha = 1.0): Graphics {

    const graphic = new Graphics();

    graphic.beginFill(colour, alpha);
    graphic.drawCircle(0, 0, radius);
    graphic.endFill();

    graphic.position.x = graphic.width / 2;
    graphic.position.y = graphic.height / 2;

    return graphic;
}

function drawEllipse(width: number, height: number, colour: number, alpha = 1.0): Graphics {

    const graphic = new Graphics();

    graphic.beginFill(colour, alpha);
    graphic.drawEllipse(0, 0, width, height);
    graphic.endFill();

    graphic.position.x = graphic.width / 2;
    graphic.position.y = graphic.height / 2;

    return graphic;
}

export function getRectTexture(services: IServiceProvider, width: number, height: number, colour: number, resolution: number, alpha?: number): Texture {
    return graphicsToTexture(services, drawRect(width, height, colour, alpha), resolution);
}

export function getBoxTexture(services: IServiceProvider, boxWidth: number, boxHeight: number, lineWidth: number, colour: number, resolution: number): Texture {
    return graphicsToTexture(services, drawBox(boxWidth, boxHeight, lineWidth, colour), resolution);
}

export function getCircleTexture(services: IServiceProvider, radius: number, colour: number, resolution: number, alpha?: number): Texture {
    return graphicsToTexture(services, drawCircle(radius, colour, alpha), resolution);
}

export function getEllipseTexture(services: IServiceProvider, width: number, height: number, colour: number, resolution: number, alpha?: number): Texture {
    return graphicsToTexture(services, drawEllipse(width, height, colour, alpha), resolution);
}

export function graphicsToTexture(services: IServiceProvider, graphics: Graphics, resolution: number) {

    const renderTexture = RenderTexture.create({
        width: graphics.width,
        height: graphics.height,
        scaleMode: SCALE_MODES.LINEAR,
        resolution
    });

    services.application.renderer.render(graphics, { renderTexture });

    return renderTexture;
}

export function generateTarget(services: IServiceProvider, spec: CrossbowBonusSpec): Spine {

    const { factory } = services;

    const container = new Graphics();

    const spine = factory.createSpine(spec.targetSpine, container);

    return spine;
}

export function generateBow(services: IServiceProvider, width: number, height: number, resolution: number): Sprite {

    const { application, factory } = services;

    const container = new Graphics();

    factory.createSpriteFromTexture(
        getRectTexture(services, width, height, 0x753d02, resolution),
        { anchor: { x: 0.5, y: 0.1 } },
        container
    );

    factory.createSpriteFromTexture(
        getEllipseTexture(services, height, width, 0x753d02, resolution),
        { anchor: { x: 0.5, y: 0 } },
        container
    );

    factory.createSpriteFromTexture(
        getRectTexture(services, width / 2, height, 0x666666, resolution),
        { anchor: { x: 0.5, y: 0 } },
        container
    );

    const containerTexture = application.renderer.generateTexture(container, { scaleMode: SCALE_MODES.LINEAR, resolution });
    return factory.createSpriteFromTexture(containerTexture, { x: 0, y: 0, anchor: 0.5 });
}

export function generateButtonSprite(services: IServiceProvider, text: string, width: number, height: number, colour: number,
    resolution: number): Sprite {

    const { application, factory } = services;

    const container = new Graphics();
    factory.createSpriteFromTexture(
        getRectTexture(services, width, height, colour, resolution),
        { anchor: 0.5 },
        container
    );

    factory.createText(text, {
        "x": 0,
        "y": 10,
        "anchor": 0.5,
        "style": {
            "fontSize": 5,
            "fill": `#FFFFFF`,
            "resolutionMultiplier": 4
        }
    }, container);

    const conTainertexture = application.renderer.generateTexture(container, { scaleMode: SCALE_MODES.LINEAR, resolution });
    return factory.createSpriteFromTexture(conTainertexture, { x: 0, y: 0, anchor: 0.5 });
}

export function setSpineSkins(spine: Spine, skinNames: string[]): void {

    const combined = new Skin(`combined`);
    const spine4 = (spine as Spine4);

    skinNames.forEach(name => combined.addSkin(spine4.skeleton.data.findSkin(name)));

    spine4.skeleton.setSkin(combined);
    spine4.skeleton.setToSetupPose();
}

export function addToSpineSlot(spine: Spine, displayObject: DisplayObject, slotName: string) {

    const slotIndex = spine.skeleton.findSlotIndex(slotName);

    if (slotIndex >= 0) {
        (spine as any).children[slotIndex].addChild(displayObject);

    } else {
        throw new Error(`addToSpineSlot - Slot not found: ` + slotName);
    }
}

export function resetSpine(spine: Spine): void {

    spine.state.clearTracks();
    spine.state.clearListeners();
    spine.skeleton.setToSetupPose();
}

export function randomCoreSymbolID(): CoreSymbolID {
    const values = Object.values(CoreSymbolID);
    const value = sample(values);
    if (value) {
        return value;
    }
    else {//shouldnt happen
        return CoreSymbolID.F6;
    }
}